// File: /var/www/app2/frontend/src/App.tsx

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import Dashboard from "./components/Dashboard";
import Reports from "./components/Reports";
import ReportDetail from "./components/ReportDetail";
import MissionaryManagement from "./components/MissionaryManagement";
import UserManagement from "./components/UserManagement";
import SupporterManagement from "./components/SupporterManagement";
import SupportRequests from "./components/SupportRequests";
import SecurityDashboard from "./components/SecurityDashboard";
import MediaLibrary from "./components/MediaLibrary";
import CountrySwitcher from "./components/CountrySwitcher";
import CountryAdmin from "./components/CountryAdmin";
import TagAdmin from "./components/TagAdmin";
import PasswordChange from "./components/PasswordChange";
import Help from "./components/Help";
import axios from "axios";
import "./App.css";

// User interface matching backend SessionUser type
export interface User {
  id: number;
  username: string;
  role: "superadmin" | "admin" | "supporter" | "system_admin" | "country_manager" | "field_user";
  fullName: string;
  firstName: string;
  lastName: string;
  organization?: string;
  email?: string;
}

// Configure axios defaults
axios.defaults.baseURL =
  process.env.NODE_ENV === "production"
    ? "" // Empty string means same domain in production
    : "http://localhost:3002";
axios.defaults.withCredentials = true; // CRITICAL: This ensures cookies are sent

// The active country is stored per-tab in sessionStorage (NOT localStorage
// and NOT the session cookie -- both of those are shared by every tab of the
// same browser, which is exactly what used to cause switching country in one
// tab to silently change what another open tab saw). sessionStorage is the
// one client-side store that's naturally isolated per tab.
export const ACTIVE_COUNTRY_STORAGE_KEY = "hoa_active_country_id";

// Add request interceptor: attach this tab's active country (if any) to
// every request, and log for debugging.
axios.interceptors.request.use(
  (config) => {
    console.log("Making request to:", config.url);
    const tabCountryId = sessionStorage.getItem(ACTIVE_COUNTRY_STORAGE_KEY);
    if (tabCountryId) {
      (config.headers as Record<string, string>)["X-Active-Country-Id"] = tabCountryId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login on 401
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [activeCountryId, setActiveCountryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get("/api/auth/check");
      const authData = response.data.data || response.data;
      if (authData.authenticated) {
        setUser(authData.user);
        // Seed this tab's country from the server-side default ONLY if this
        // tab hasn't already picked one -- once a tab has its own value, it
        // stays independent of what other tabs (or a later login) set.
        if (sessionStorage.getItem(ACTIVE_COUNTRY_STORAGE_KEY) === null && authData.activeCountryId) {
          sessionStorage.setItem(ACTIVE_COUNTRY_STORAGE_KEY, String(authData.activeCountryId));
        }
        const tabCountryId = sessionStorage.getItem(ACTIVE_COUNTRY_STORAGE_KEY);
        setActiveCountryId(tabCountryId ? parseInt(tabCountryId, 10) : null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData: User) => {
    setUser(userData);
    checkAuth();
  };

  const handleLogout = async () => {
    // Clear this tab's stored country so a different user logging in on the
    // same tab afterward doesn't inherit a country they may not even be
    // assigned to.
    sessionStorage.removeItem(ACTIVE_COUNTRY_STORAGE_KEY);
    try {
      await axios.post("/api/auth/logout");
      setUser(null);
      window.location.href = "/login"; // Hard redirect to clear any cached state
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    console.log("⏳ Loading...");
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        {!isOnline && (
          <div style={{ background: "#744210", color: "#fefcbf", textAlign: "center",
            padding: "6px 16px", fontSize: 13, fontWeight: 500 }}>
            You are offline — GPS capture works, but data won't sync until you reconnect.
          </div>
        )}
        {user && (
          <nav className="navbar">
            <div className="nav-container">
              <div className="nav-top-row">
                <h1 style={{ display: "flex", alignItems: "center", gap: 12, margin: 0 }}>
                  <img src="/hoa-logo.png" alt="Horn of Africa" style={{ height: 36 }} />
                  Horn of Africa
                </h1>
                <div className="nav-user-row">
                  <CountrySwitcher />
                  <span className="user-info">{user.fullName || user.username}</span>
                  <Link to="/change-password" className="nav-link">Change Password</Link>
                  <button onClick={handleLogout} className="logout-btn">Logout</button>
                </div>
              </div>
              <div className="nav-links-row">
                <Link to="/" className="nav-link">Dashboard</Link>
                <Link to="/reports" className="nav-link">Reports</Link>
                <Link to="/missionaries" className="nav-link">Missionaries</Link>
                {(user.role === "admin" || user.role === "superadmin" || user.role === "system_admin") && (
                  <>
                    {user.role === "system_admin" && (
                      <Link to="/admin/countries" className="nav-link">Countries</Link>
                    )}
                    <Link to="/users" className="nav-link">Users</Link>
                    <Link to="/supporters" className="nav-link">Supporters</Link>
                    <Link to="/support-requests" className="nav-link">Support Requests</Link>
                    <Link to="/media" className="nav-link">Media Library</Link>
                    <Link to="/admin/tags" className="nav-link">Reference Lists</Link>
                  </>
                )}
                {user.role === "superadmin" && (
                  <Link to="/security" className="nav-link">Security</Link>
                )}
                <Link to="/help" className="nav-link">Help</Link>
              </div>
            </div>
          </nav>
        )}

        <Routes>
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />
            }
          />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/"
            element={
              !user ? <Navigate to="/login" /> : <Dashboard user={user} activeCountryId={activeCountryId} />
            }
          />

          <Route
            path="/reports"
            element={!user ? <Navigate to="/login" /> : <Reports user={user} />}
          />

          <Route
            path="/reports/:id"
            element={
              !user ? <Navigate to="/login" /> : <ReportDetail user={user} />
            }
          />

          <Route
            path="/missionaries"
            element={
              !user ? (
                (() => {
                  console.log("🚫 /missionaries - No user, redirecting to login");
                  return <Navigate to="/login" />;
                })()
              ) : (
                (() => {
                  console.log("✅ /missionaries - User authenticated, rendering MissionaryManagement");
                  return <MissionaryManagement user={user} />;
                })()
              )
            }
          />

          <Route
            path="/users"
            element={
              !user ? <Navigate to="/login" /> : <UserManagement user={user} />
            }
          />

          <Route
            path="/supporters"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SupporterManagement user={user} />
              )
            }
          />

          <Route
            path="/support-requests"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SupportRequests user={user} />
              )
            }
          />

          <Route
            path="/media"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : user.role === "admin" || user.role === "superadmin" || user.role === "system_admin" ? (
                <MediaLibrary user={user} />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/security"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SecurityDashboard user={user} />
              )
            }
          />

          <Route
            path="/change-password"
            element={!user ? <Navigate to="/login" /> : <PasswordChange />}
          />

          <Route
            path="/help"
            element={!user ? <Navigate to="/login" /> : <Help user={user} />}
          />

          <Route
            path="/admin/countries"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : user.role === "system_admin" ? (
                <CountryAdmin />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/admin/tags"
            element={
              !user ? <Navigate to="/login" /> :
              (user.role === "admin" || user.role === "superadmin" || user.role === "system_admin") ?
                <TagAdmin /> : <Navigate to="/" />
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
